/**
 * DEPLETIONS-INVENTORY MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// importTemplates.js — generates pre-formatted Excel templates for the import
// flow. The dimension columns (State, Distributor, Flavor, Bottle Size, Package)
// are PRE-FILLED from the customer's own reference data — every active product ×
// every sales state — so each row is guaranteed to match on import. The value
// columns (monthly case-sales for depletions; Current Inventory for inventory)
// are left blank for the user to fill. Blank value cells are skipped on import,
// so a partially-filled template never creates zero/garbage rows.
//
// Header names match exactly what the import parsers look for
// (see DepletionsPage / InventoryPage buildPreview).

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// SheetJS on demand (shared with the pages' import/export).
async function loadXLSX() {
  const m = await import("xlsx");
  return m.read ? m : (m.default || m);
}

// One template row's dimension values for each (state, product) combo.
function dimensionRows(states, products) {
  const active = (products || []).filter((p) => p.active !== false);
  const rows = [];
  for (const st of states || []) {
    for (const p of active) {
      rows.push({
        distributor: st.distributor || "",
        state: st.state_name || "",
        flavor: p.flavor?.name || "",
        size: p.size?.name || "",
        pkg: p.pack_size?.name || "",
      });
    }
  }
  return rows;
}

function triggerDownload(XLSX, wb, filename) {
  XLSX.writeFile(wb, filename);
}

/** Depletions template: pivot layout with month columns for the current year. */
export async function downloadDepletionsTemplate({ states = [], products = [] } = {}) {
  const XLSX = await loadXLSX();
  const year = new Date().getFullYear();
  const yy = String(year).slice(-2);
  const monthCols = MONTH_ABBR.map((m) => `${m}-${yy}`);
  const headers = ["Distributor", "State", "Flavor", "Bottle Size", "Package", ...monthCols];

  const dims = dimensionRows(states, products);
  const body = dims.length
    ? dims.map((d) => [d.distributor, d.state, d.flavor, d.size, d.pkg, ...monthCols.map(() => "")])
    : [["Example Distributor", "California", "Original", "750ml", "6pk", ...monthCols.map(() => "")]];

  const ws = XLSX.utils.aoa_to_sheet([headers, ...body]);
  ws["!cols"] = headers.map((_, i) => ({ wch: i < 5 ? 18 : 8 }));
  ws["!freeze"] = { xSplit: 0, ySplit: 1 };
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Depletions");
  triggerDownload(XLSX, wb, `Depletions_Template_${year}.xlsx`);
}

/** Inventory template: one row per (state, product) with a Current Inventory column. */
export async function downloadInventoryTemplate({ states = [], products = [] } = {}) {
  const XLSX = await loadXLSX();
  const headers = ["State", "Distributor", "Flavor", "Bottle Size", "Package", "Current Inventory"];

  const dims = dimensionRows(states, products);
  const body = dims.length
    ? dims.map((d) => [d.state, d.distributor, d.flavor, d.size, d.pkg, ""])
    : [["California", "Example Distributor", "Original", "750ml", "6pk", ""]];

  const ws = XLSX.utils.aoa_to_sheet([headers, ...body]);
  ws["!cols"] = headers.map((_, i) => ({ wch: i < 5 ? 18 : 16 }));
  ws["!freeze"] = { xSplit: 0, ySplit: 1 };
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Inventory");
  triggerDownload(XLSX, wb, `Inventory_Template.xlsx`);
}
